/**
 * React hook wrapping the Pipecat RTVIClient + SmallWebRTC transport.
 *
 * Owns:
 *  - the singleton client lifecycle for the panel
 *  - React-visible state (status, transcript log, live partial, audio levels)
 *  - the hidden <audio> sink that plays Gemini's voice on the user's speakers
 *
 * Mirrors the standalone widget's voiceClient.ts but in plain JS, scoped
 * to the noki-fe panel and with bridge / control-plane / events stripped
 * (those land in later branches).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RTVIClient } from "@pipecat-ai/client-js";
import { SmallWebRTCTransport } from "@pipecat-ai/small-webrtc-transport";

import { getCopilotAuthToken } from "./authToken";
import { formatResetTime } from "./resetTime";

const newTurnId = () =>
  // The hook is browser-only (Pipecat RTCPeerConnection, getUserMedia, etc.),
  // so `window` is safe and avoids CRA ESLint's `no-undef` on `globalThis`.
  (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const makeFinalTurn = (role, text) => ({
  id: newTurnId(),
  role,
  text,
  startedAt: Date.now(),
});

const appendChunk = (current, chunk) =>
  (current ? `${current} ${chunk}` : chunk).replace(/\s{2,}/g, " ");

/**
 * Map an /api/offer rejection to a clinician-friendly message. When a
 * governance limit is hit the server rejects the offer with a structured body
 * ({reason, detail, ...}); depending on how the transport surfaces the HTTP
 * error, that reason may land on the error object or only inside its text, so
 * we scan the stringified error for the known reason codes. Returns null when
 * it's not a recognised governance rejection (caller keeps its own fallback).
 */
const friendlyOfferError = (raw) => {
  let blob = "";
  if (typeof raw === "string") {
    blob = raw;
  } else if (raw) {
    try {
      blob = JSON.stringify(raw);
    } catch {
      blob = String(raw);
    }
    if (raw.message) blob += ` ${raw.message}`;
  }
  // The 402/429 body carries an ISO `resets_at`; when the transport exposed
  // the body text (best-effort, same as the reason scan) surface the actual
  // resume time in the viewer's local zone.
  const resetMatch = blob.match(/"resets_at"\s*:\s*"([^"]+)"/);
  const resumesAt = resetMatch ? formatResetTime(resetMatch[1]) : null;

  // Auth rejections (only when the copilot server has auth enabled). The
  // server answers /api/offer with 401 {code} when the Firebase token is
  // missing/expired/invalid — surface a sign-in prompt instead of a generic
  // "couldn't connect", so the clinician knows the fix is to sign in again.
  if (/\btoken_expired\b|\btoken_revoked\b/.test(blob)) {
    return "Your session expired. Please sign in again to use the voice copilot.";
  }
  if (/\bmissing_token\b|\btoken_invalid\b/.test(blob)) {
    return "Please sign in to use the voice copilot.";
  }
  if (/concurrency_limit/.test(blob)) {
    // Concurrency frees when a session ends — no fixed reset time.
    return (
      "You've reached the maximum number of simultaneous voice sessions. "
      + "Close one and try again."
    );
  }
  if (/\bcooldown\b/.test(blob)) {
    return resumesAt
      ? `You've just finished a voice session. Voice resumes ${resumesAt}.`
      : "You've just finished a voice session. Please wait a moment before "
        + "starting a new one.";
  }
  if (
    /budget_exhausted|quota_exceeded|daily_budget|daily_cost|daily_minutes/.test(
      blob,
    )
  ) {
    return resumesAt
      ? "You've reached today's voice usage limit for your plan. Voice "
        + `resumes ${resumesAt}.`
      : "You've reached today's voice usage limit for your plan. Voice will "
        + "be available again after the daily reset.";
  }
  return null;
};

/**
 * @param {string} serverUrl  Base URL of the copilot server.
 * @param {string} sessionId  The panel's control-plane session_id
 *        (used for diagnostic logging only — the server no longer
 *        accepts session_id directly in the offer body).
 *
 * The pair_token the offer needs is supplied through the returned
 * ``setPairToken`` setter. The panel calls this from a useEffect
 * synced to ``controlSocket.pairToken`` once the WS session.ready
 * arrives. This indirection mirrors the existing ``sendToolResultRef``
 * pattern in NokiCopilotPanel: ``useVoiceClient`` and
 * ``useControlSocket`` produce each other's required wires, and the
 * panel marries them via refs after both hooks have run.
 */
export function useVoiceClient(serverUrl, sessionId) {
  const clientRef = useRef(null);
  const botAudioElementRef = useRef(null);
  const assistantPartialRef = useRef("");

  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [livePartial, setLivePartial] = useState(null);
  // Audio levels are a ~30Hz signal. They are held in REFS (not React state)
  // so the pipecat level callbacks never re-render the panel — a 30Hz panel
  // re-render used to starve the main thread and stall host-app views while
  // listening. The AudioVisualizer reads these refs via its own rAF loop and
  // paints imperatively. Only turn/status changes re-render the panel.
  const userLevelRef = useRef(0);
  const assistantLevelRef = useRef(0);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  // Local-track + remote-audio mute state (clinical-safety: copilot
  // MUST not listen or speak during an active encounter recording).
  // The encounter capture and the copilot share the browser mic via
  // getUserMedia — without this gate, the copilot's Gemini hears the
  // patient/clinician conversation and may fire false-positive tool
  // calls (e.g. patient says 'stop bleeding' -> stop_recording).
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [muteReason, setMuteReason] = useState(null);

  // Mount a hidden <audio> sink for the bot voice. Pipecat's small-webrtc
  // transport only fires onTrackStarted — it doesn't auto-attach the remote
  // track to a media element. Without this the user hears nothing.
  useEffect(() => {
    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.setAttribute("playsinline", "");
    audio.style.display = "none";
    document.body.appendChild(audio);
    botAudioElementRef.current = audio;
    return () => {
      audio.srcObject = null;
      audio.remove();
      botAudioElementRef.current = null;
    };
  }, []);

  // Default to same-origin; CRA's setupProxy.js forwards /api/* and /ws/*
  // to the Pipecat server. Hardcoding localhost:7860 here would force
  // every page-side request cross-origin and Pipecat's WebRTC handshake
  // doesn't survive that — ICE stalls in "checking" until the 60s timeout.
  const baseUrl = useMemo(
    () => serverUrl || (typeof window !== "undefined" ? window.location.origin : ""),
    [serverUrl],
  );

  // Server-issued pair token captured from the WS session.ready
  // frame, supplied by the panel via setPairToken(...). Held in a
  // ref so a token rotation between client construction and the
  // next /api/offer POST always sees the freshest value. null
  // until the WS handshake completes; cleared on WS disconnect.
  const pairTokenRef = useRef(null);
  const setPairToken = useCallback((token) => {
    pairTokenRef.current = token || null;
  }, []);

  // Audio-level pause flag (route-transition mitigation).
  //
  // Pipecat fires onLocalAudioLevel / onRemoteAudioLevel ~30 times
  // per second. Each call triggers setUserLevel / setAssistantLevel,
  // which re-renders the visualizer subtree of the panel. While the
  // user is navigating to a new route — especially the first time in
  // a dev session, when CRA has to compile the route's lazy chunk on
  // demand — that re-render rate eats main-thread time the browser
  // could otherwise spend parsing + initializing the new page.
  //
  // The panel calls setAudioLevelsPaused(true) on every pathname
  // change and clears it ~1.5s later. While paused, the level
  // callbacks no-op and the levels stay at zero. The visualizer
  // shows a calm flat bar instead of live audio, which is the
  // intended visual: when navigation is in flight, "audio is settling".
  //
  // Held in a ref so the gate doesn't itself cause re-renders.
  const audioLevelsPausedRef = useRef(false);
  const setAudioLevelsPaused = useCallback((paused) => {
    const want = !!paused;
    audioLevelsPausedRef.current = want;
    if (want) {
      // Snap the displayed levels to zero so the bar visibly settles
      // instead of freezing at whatever the last sample was.
      userLevelRef.current = 0;
      assistantLevelRef.current = 0;
    }
  }, []);

  const buildClient = useCallback((authToken) => {
    // STUN is required even on a same-host dev setup: Chrome offers `*.local`
    // mDNS hostnames as host candidates by default, which aiortc on macOS
    // doesn't reliably resolve, so the only candidate pair the server can
    // actually reach is Chrome's `srflx`. SmallWebRTCTransport defaults
    // `iceServers` to [], so we have to supply them explicitly. Google's
    // public STUN is what Pipecat's own docs use.
    const transport = new SmallWebRTCTransport({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ],
    });
    // `requestData` is merged into the JSON body of every endpoint
    // call RTVIClient makes (the initial RTVI handshake POST and the
    // SDP-bearing offer POST). Post-pair-token v4 (2026-05-28): we
    // forward the server-issued `pair_token` here. The server verifies
    // the token's HMAC signature, expiry, single-use jti, and
    // user-binding before binding any Pipecat pipeline. There is no
    // longer a `copilot_session_id` fallback — a missing or invalid
    // pair_token gets a 401 from the server (rejected at the door).
    //
    // We read from pairTokenRef so a token refresh after this client
    // is built is still picked up on the next offer the transport
    // makes (e.g. ICE restart, renegotiate). Sending `undefined` here
    // when no token is available would result in a 422 from the
    // server's pydantic validation; the caller (`connect()` below)
    // refuses to even start the flow in that state.
    const currentPairToken = pairTokenRef.current;
    const requestData = currentPairToken
      ? { pair_token: currentPairToken }
      : undefined;

    // `headers` are sent on every POST RTVIClient + the WebRTC
    // transport make to /api/offer (RTVI handshake + the SDP offer).
    // Must be a `Headers` instance, not a plain object — RTVIClient's
    // handshake calls `.entries()` directly on this value (see
    // node_modules/@pipecat-ai/client-js/dist/index.js around the
    // headers spread). A plain object throws TypeError there and the
    // bare default-message `StartBotError` ("Failed to connect /
    // invalid auth bundle from base url") fires. Our patched
    // SmallWebRTCTransport wraps the value in `new Headers(...)` so
    // it accepts either shape on the SDP-offer path.
    const headers = authToken
      ? new Headers({ Authorization: `Bearer ${authToken}` })
      : undefined;

    return new RTVIClient({
      transport,
      enableMic: true,
      enableCam: false,
      timeout: 60_000,
      params: {
        baseUrl,
        endpoints: { connect: "/api/offer" },
        ...(requestData ? { requestData } : {}),
        ...(headers ? { headers } : {}),
      },
      callbacks: {
        onConnected: () => {
          setStatus("connected");
          setErrorMessage(null);
        },
        onDisconnected: () => {
          setStatus("idle");
          userLevelRef.current = 0;
          assistantLevelRef.current = 0;
          setIsAssistantSpeaking(false);
          setLivePartial(null);
          assistantPartialRef.current = "";
        },
        onError: (event) => {
          const raw =
            event && typeof event === "object" && "data" in event
              ? event.data
              : event;
          // Never fall through to String(obj) → "[object Object]".
          // Structured error payloads carry the real text on a field.
          let detail = "";
          if (typeof raw === "string") {
            detail = raw;
          } else if (raw && typeof raw === "object") {
            detail =
              raw.message ||
              raw.error_message ||
              raw.error ||
              raw.detail ||
              raw.reason ||
              "";
            if (!detail) {
              try {
                detail = JSON.stringify(raw);
              } catch (e) {
                detail = "";
              }
            }
          }
          setErrorMessage(
            friendlyOfferError(raw) || detail || "Voice session failed.",
          );
          setStatus("error");
        },
        onBotStartedSpeaking: () => {
          setIsAssistantSpeaking(true);
          assistantPartialRef.current = "";
        },
        onBotStoppedSpeaking: () => {
          setIsAssistantSpeaking(false);
          assistantLevelRef.current = 0;
          // Discard leftover TTS chunks — `bot-transcript` is the sole
          // authority for chat bubbles. Pipecat emits `bot-tts-text`
          // chunks paced with audio playback, so they keep arriving
          // AFTER the last `bot-transcript`; flushing here would create
          // a duplicate trailing bubble.
          assistantPartialRef.current = "";
          setLivePartial(null);
        },
        onUserTranscript: (data) => {
          // Only string transcripts become turns — guards against a
          // structured frame rendering as "[object Object]".
          const text = typeof data?.text === "string" ? data.text : "";
          if (!text) return;
          if (data.final) {
            setLivePartial(null);
            setTranscript((turns) => [...turns, makeFinalTurn("user", text)]);
          } else {
            setLivePartial({ role: "user", text });
          }
        },
        onBotTranscript: (data) => {
          const text = typeof data?.text === "string" ? data.text : "";
          if (!text) return;
          assistantPartialRef.current = "";
          setLivePartial(null);
          setTranscript((turns) => [...turns, makeFinalTurn("assistant", text)]);
        },
        onBotTtsText: (data) => {
          const text = typeof data?.text === "string" ? data.text : "";
          if (!text) return;
          assistantPartialRef.current = appendChunk(
            assistantPartialRef.current,
            text,
          );
          setLivePartial({
            role: "assistant",
            text: assistantPartialRef.current,
          });
        },
        onLocalAudioLevel: (level) => {
          // Ref write only — no setState, so the ~30Hz level never re-renders
          // the panel (the AudioVisualizer paints from this ref via rAF).
          if (audioLevelsPausedRef.current) return;
          userLevelRef.current = typeof level === "number" ? level : 0;
        },
        onRemoteAudioLevel: (level) => {
          if (audioLevelsPausedRef.current) return;
          assistantLevelRef.current = typeof level === "number" ? level : 0;
        },
        onTrackStarted: (track, participant) => {
          if (track?.kind !== "audio") return;
          if (participant?.local) return;
          const audio = botAudioElementRef.current;
          if (!audio) return;
          audio.srcObject = new MediaStream([track]);
          audio.play().catch((err) => {
            const detail = err instanceof Error ? err.message : String(err);
            setErrorMessage(`Bot audio playback blocked: ${detail}`);
          });
        },
      },
    });
  }, [baseUrl]);

  const connect = useCallback(async () => {
    if (status === "connecting" || status === "connected") return;

    // Zero-fallback rule (pair-token v4): refuse to start the flow if
    // we don't have a server-issued pair_token yet. The control-plane
    // WS handshake mints it as part of session.ready; if a click
    // lands before that arrives, we surface a clear error to the
    // user instead of firing an offer the server is guaranteed to
    // 401. Common cause: the panel was opened and the user clicked
    // mic almost instantly, racing the WS open.
    if (!pairTokenRef.current) {
      setErrorMessage(
        "Voice channel isn't ready yet. Wait a moment and try again.",
      );
      setStatus("error");
      // eslint-disable-next-line no-console
      console.warn("[copilot] voice.connect.no_pair_token", {
        sessionId,
        detail:
          "connect() refused — pair_token not yet captured from WS "
          + "session.ready. The server would 401 the offer.",
      });
      return;
    }

    setStatus("connecting");
    setErrorMessage(null);
    setTranscript([]);
    setLivePartial(null);
    assistantPartialRef.current = "";

    try {
      // Fetch a fresh-enough Firebase ID token before constructing
      // the RTVIClient. Firebase auto-refreshes the cached token when
      // it's within 5 min of expiry; we don't force a refresh per
      // connect because that's a needless round-trip when the cache
      // is warm. Token may be `null` for unauthenticated users — the
      // server's dev mode (auth_required=false) accepts that;
      // production mode will return 401.
      const authToken = await getCopilotAuthToken();

      // Pre-flight: ask the server whether a new session is allowed BEFORE we
      // attempt the WebRTC handshake. The transport surfaces a rejected
      // /api/offer only as an opaque error, so a daily-limit / cool-down block
      // would otherwise read as a generic "failed to start". This read-only
      // check lets us show the precise reason (and avoids burning a pair token
      // on a doomed offer). Best-effort: any preflight error falls through and
      // lets the offer itself decide, so a preflight blip never blocks a start.
      try {
        const pfHeaders = { Accept: "application/json" };
        if (authToken) pfHeaders.Authorization = `Bearer ${authToken}`;
        const pf = await fetch(`${baseUrl.replace(/\/+$/, "")}/api/preflight`, {
          method: "GET",
          headers: pfHeaders,
        });
        if (pf.ok) {
          const body = await pf.json();
          if (body && body.can_start === false) {
            const msg =
              friendlyOfferError(JSON.stringify(body))
              || "Voice is unavailable right now. Please try again later.";
            setErrorMessage(msg);
            setStatus("error");
            return;
          }
        }
      } catch {
        /* preflight is best-effort — proceed and let the offer decide */
      }

      const client = buildClient(authToken);
      clientRef.current = client;
      await client.connect();
    } catch (error) {
      const message =
        friendlyOfferError(error)
        || (error instanceof Error
          ? error.message
          : "Failed to start the voice session.");
      setErrorMessage(message);
      setStatus("error");
      const failed = clientRef.current;
      clientRef.current = null;
      if (failed) {
        try {
          await failed.disconnect();
        } catch {
          /* swallow — already failed */
        }
      }
    }
  }, [buildClient, sessionId, status, baseUrl]);

  // Mute / unmute the LOCAL microphone track (what the copilot sends to
  // Pipecat) and the REMOTE bot audio (what the user hears). Used as
  // the Layer-1 clinical-safety gate when an encounter recording is
  // active — see PatientWrapper / recordingStatus subscription.
  //
  // Implementation: walk the RTCPeerConnection's audio senders and
  // toggle `track.enabled`. WebRTC stays connected (instant resume),
  // but no audio frames flow upstream — the copilot's Gemini never
  // hears the encounter conversation. The bot-audio element is muted
  // so the agent's TTS doesn't speak over the clinician/patient.
  //
  // Pipecat's RTVIClient does not expose a stable public mute API
  // (varies across versions); reaching into transport.peerConnection
  // is the lowest-risk fallback that works for both 0.4.x and 1.x
  // versions of @pipecat-ai/small-webrtc-transport. If a future
  // version exposes `client.enableMic(false)` we'll switch to that.
  const setMicMuted = useCallback((mute, reason) => {
    const wantMute = !!mute;
    setIsMicMuted(wantMute);
    setMuteReason(wantMute ? reason || null : null);

    const client = clientRef.current;
    const transport = client?.transport;
    const pc =
      transport?.peerConnection
      || transport?._pc
      || transport?.pc
      || null;

    if (pc && typeof pc.getSenders === "function") {
      try {
        for (const sender of pc.getSenders()) {
          if (sender?.track && sender.track.kind === "audio") {
            // eslint-disable-next-line no-param-reassign
            sender.track.enabled = !wantMute;
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[copilot] setMicMuted: failed to toggle local senders", err);
      }
    }

    // Also mute the remote (bot) audio element so the agent's TTS
    // doesn't speak during recording. Browsers honour element.muted
    // immediately even if the underlying srcObject keeps producing
    // audio — no audible playback while muted.
    if (botAudioElementRef.current) {
      botAudioElementRef.current.muted = wantMute;
    }

    // Drop the live partial display when muted so a stale "..."
    // doesn't linger on screen.
    if (wantMute) {
      setLivePartial(null);
      userLevelRef.current = 0;
    }
  }, []);

  const disconnect = useCallback(async () => {
    const client = clientRef.current;
    if (!client) {
      setStatus("idle");
      return;
    }
    setStatus("disconnecting");
    try {
      await client.disconnect();
    } finally {
      clientRef.current = null;
      setStatus("idle");
      userLevelRef.current = 0;
      assistantLevelRef.current = 0;
      setIsAssistantSpeaking(false);
      setLivePartial(null);
      assistantPartialRef.current = "";
      if (botAudioElementRef.current) {
        botAudioElementRef.current.srcObject = null;
      }
    }
  }, []);

  // Hard cleanup on unmount: never leave a peer connection alive.
  useEffect(() => {
    return () => {
      const client = clientRef.current;
      clientRef.current = null;
      if (client) {
        client.disconnect().catch(() => {
          /* unmount path — nothing to do */
        });
      }
    };
  }, []);

  // Pull assistant level back to zero when speech stops so the visualizer
  // doesn't freeze on the last sample.
  useEffect(() => {
    if (!isAssistantSpeaking) {
      const handle = window.setTimeout(() => assistantLevelRef.current = 0, 200);
      return () => window.clearTimeout(handle);
    }
    return undefined;
  }, [isAssistantSpeaking]);

  // Clinical-safety reapply: when the WebRTC connection transitions
  // to "connected" — including after a reconnect — re-apply the
  // current mute state. Without this, a panel that reconnects mid-
  // recording would come back unmuted (track.enabled defaults true on
  // a fresh peer-connection's senders) and briefly leak audio
  // upstream until the next setMicMuted call. Watching `status` +
  // `isMicMuted` together is the structural fix.
  useEffect(() => {
    if (status !== "connected") return;
    const client = clientRef.current;
    const transport = client?.transport;
    const pc =
      transport?.peerConnection
      || transport?._pc
      || transport?.pc
      || null;
    if (pc && typeof pc.getSenders === "function") {
      try {
        for (const sender of pc.getSenders()) {
          if (sender?.track && sender.track.kind === "audio") {
            // eslint-disable-next-line no-param-reassign
            sender.track.enabled = !isMicMuted;
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[copilot] reapply mute on connected: failed", err);
      }
    }
    if (botAudioElementRef.current) {
      botAudioElementRef.current.muted = isMicMuted;
    }
  }, [status, isMicMuted]);

  return {
    status,
    errorMessage,
    transcript,
    livePartial,
    userLevelRef,
    assistantLevelRef,
    isAssistantSpeaking,
    isMicMuted,
    muteReason,
    setMicMuted,
    connect,
    disconnect,
    // Plumbing for the pair token captured by useControlSocket. The
    // panel calls this in a useEffect synced to
    // controlSocket.pairToken so the value is fresh by the time the
    // user clicks "mic". connect() refuses if the ref is null.
    setPairToken,
    // Lets the panel pause audio-level state updates during route
    // transitions so the main thread is free for chunk parse + page
    // mount. See audioLevelsPausedRef comment for the full rationale.
    setAudioLevelsPaused,
  };
}
